import React, { useEffect, useRef, useState } from 'react';
import { Button, Divider, Form, Input, Layout, message, Typography } from 'antd';
import 'react-intl-tel-input/dist/main.css';
import PhoneInput from 'react-phone-input-2';
import { useDispatch } from 'react-redux';
import useCountryCode from '../../hooks/useCountryCode';
import { GOOGLE_AUTH_LINK } from '../../constants';
import { useTranslation } from 'react-i18next';
import { initiateWhatsappLogin, submitWhatsappToken } from '../../store/actions/authActions';
import './style.less';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import mobileCheck from '../../utils/mobileCheck';
import { Loading3QuartersOutlined, LoadingOutlined, WhatsAppOutlined } from '@ant-design/icons';

import TokenInput from './TokenInput';
import { ChevronLeft } from 'react-feather';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { getPlatform } from '../../utils/helpers';
import { storeAuthToken } from '../../utils/token';

import packageJson from '../../../package.json';

// countries that have auth link issue
const OTP_COUNTRIES = ['51', '52', '54', '55', '56', '57', '58', '593', '598', '595', '591'];

export const DEMO_NUMBER = '14242424242';

const isMobile = mobileCheck();

const platform = getPlatform();

const Login = () => {
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const countryCode = useCountryCode();
  const [screenId, setScreenId] = useState(0);

  function handlePhoneNumberChange(value) {
    setWhatsappNumber(value);
  }

  const screens = [
    <LoginScreen
      key={0}
      whatsappNumber={whatsappNumber}
      countryCode={countryCode}
      handlePhoneNumberChange={handlePhoneNumberChange}
      setScreenId={setScreenId}
    />,
    <WaitingScreen key={1} whatsappNumber={whatsappNumber} setScreenId={setScreenId} />,
    <TokenScreen key={2} whatsappNumber={whatsappNumber} setScreenId={setScreenId} />,
  ];

  return (
    <Layout>
      <Layout.Content className="site-login-content">{screens[screenId]}</Layout.Content>
    </Layout>
  );
};

const LoginScreen = ({ whatsappNumber, countryCode, handlePhoneNumberChange, setScreenId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const redirectToWhatsappLink =
    'https://api.whatsapp.com/send?phone=' +
    '13024494100' +
    '&text=' +
    encodeURIComponent('Hey WhatsForm 👋, sign me in!');

  async function handleLoginClick() {
    if (whatsappNumber.slice(0, 2) === '90' || whatsappNumber.slice(0, 3) === '599') {
      message.info(
        `Sorry, WhatsApp login is not supported on your number. Don't worry, you can still use WhatsForm with your Google account.`,
        5,
      );
      return;
    }

    if (!isPossiblePhoneNumber(`+${whatsappNumber}`)) {
      message.error('Phone number is invalid', 3);
      return;
    }

    try {
      setIsLoading(true);
      await dispatch(initiateWhatsappLogin(whatsappNumber));
      // Conditionally load otp or auth link
      // if (OTP_COUNTRIES.some((code) => whatsappNumber.indexOf(code) === 0)) {
      //   setScreenId(2);
      // } else {
      //   setScreenId(1);
      // }
      if (whatsappNumber === DEMO_NUMBER) {
        setScreenId(1);
      } else {
        setScreenId(2);
      }
    } catch (e) {
      console.log(e);
      if (e?.response?.data?.error) {
        message.error(e.response?.data?.error, 3);
      } else {
        message.error('Something went wrong', 3);
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="site-login-body">
      <div className="logo">
        {platform === 'web' ? (
          <img src="/logo.png" style={{ height: '30px' }} />
        ) : (
          <img src="/whatsform-biz.png" style={{ height: '50px' }} />
        )}
      </div>
      <div>
        {platform === 'web' && isMobile ? (
          <Button
            type="primary"
            className="login-form-button"
            onClick={() => (window.location = redirectToWhatsappLink)}
          >
            <img height="24" src="/whatsapp.png" />
            <span>{'Login with WhatsApp'}</span>
          </Button>
        ) : (
          <Form>
            <Form.Item>
              <PhoneInput
                value={whatsappNumber}
                onChange={handlePhoneNumberChange}
                country={countryCode}
                autoFormat="false"
                enableSearch="true"
                enableLongNumbers="true"
                placeholder=""
                disableSearchIcon="true"
                preferredCountries={['us', 'in', 'br', 'gb']}
              />
            </Form.Item>
            <Form.Item>
              <Button
                onClick={handleLoginClick}
                size="large"
                htmlType="submit"
                className="login-form-button"
                type="primary"
                loading={isLoading}
                disabled={isLoading}
              >
                <img height="24" src="/whatsapp.png" />
                <span>{'Login with WhatsApp'}</span>
              </Button>
            </Form.Item>
          </Form>
        )}
      </div>
      {platform === 'web' && (
        <>
          {' '}
          <Divider plain>OR</Divider>
          <a className="ant-btn google-login btn" href={GOOGLE_AUTH_LINK}>
            <img src="/google/g-logo.png" />
            <span>Login with Google</span>
          </a>
        </>
      )}
      {platform !== 'web' && (
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <Typography.Text type="secondary">{`Version ${packageJson.version}`}</Typography.Text>
        </div>
      )}
    </div>
  );
};

const WaitingScreen = ({ whatsappNumber, setScreenId }) => {
  // short polling to the server to check for changes in the user's status
  useEffect(() => {
    const interval = setInterval(async () => {
      // check for user status
      const response = await axios.get('/auth/whatsapp/status', { params: { whatsappNumber } });
      if (response.data.status === 'verified') {
        clearInterval(interval);
        await storeAuthToken(response.data.token);
        window.location.replace('/');
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [whatsappNumber]);

  return (
    <div className="waiting-screen">
      {/* <img src="/logo.png" /> */}
      <div style={{ display: 'grid', placeContent: 'center', marginBottom: '1.5rem' }}>
        <Loading3QuartersOutlined spin style={{ fontSize: '20px' }} />
      </div>
      <Typography.Title level={5} style={{ marginBottom: '2px' }}>
        Authentication link sent on{' '}
        <span style={{ color: '#2AB200' }}>
          WhatsApp <WhatsAppOutlined />
        </span>
      </Typography.Title>
      <Typography.Text>
        {/* Please check your WhatsApp number <strong>{whatsappNumber || 9961858996}</strong> for the
        link. */}
        Click the link sent to your WhatsApp to continue
      </Typography.Text>
      <Typography.Paragraph type="secondary" style={{ marginTop: '24px' }}>
        Trouble with WhatsApp?{' '}
        <a
          onClick={() => {
            setScreenId(0);
          }}
        >
          Try another method
        </a>
      </Typography.Paragraph>
    </div>
  );
};

const tokenRegex = /^[0-9]{6}$/;

const TokenScreen = ({ whatsappNumber, setScreenId }) => {
  const dispatch = useDispatch();
  const [tokenInputs, setTokenInputs] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();

  const submitTimer = useRef();

  useEffect(() => {
    async function handleWhatsappLogin(token) {
      if (tokenRegex.test(token) === false) {
        message.error('Invalid verification code', 3);
        return;
      }
      setIsLoading(true);
      try {
        await dispatch(submitWhatsappToken(whatsappNumber, token, history));
      } catch (e) {
        console.log(e);
        if (e?.response?.data?.error) {
          message.error(e.response?.data?.error, 3);
        } else {
          message.error('Something went wrong', 3);
        }
      } finally {
        setTokenInputs(['', '', '', '', '', '']);
        setIsLoading(false);
      }
    }
    clearTimeout(submitTimer.current);
    if (tokenInputs.every((item) => item)) {
      submitTimer.current = setTimeout(() => {
        handleWhatsappLogin(tokenInputs.join(''));
      }, 500);
    }
  }, [tokenInputs, whatsappNumber, dispatch, history]);

  return (
    <div className="token-input-container">
      <div className="token-input-section">
        <label style={{ color: '#222222aa', fontWeight: '600' }}>
          Enter the verification code sent to your{' '}
          <span style={{ color: '#2AB200' }}>
            WhatsApp <WhatsAppOutlined />
          </span>
        </label>
        <TokenInput
          tokenInputs={tokenInputs}
          setTokenInputs={setTokenInputs}
          isLoading={isLoading}
          tokenRegex={tokenRegex}
        />
      </div>
      <Button
        onClick={() => setScreenId(0)}
        type="link"
        icon={<ChevronLeft />}
        className="prev-button"
      >
        Go back
      </Button>
    </div>
  );
};
export default Login;
